import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Shield, Mail, Lock, User, AlertCircle, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthPage = () => {
  usePageMeta({
    title: 'Sign In - Credential Tracker',
    description: 'Sign in or create an account to access the Digitive Health Credential Tracker. Manage your healthcare certifications and licenses.',
    path: '/auth',
    noindex: true,
  });

  const [mode, setMode] = useState('login'); // login, register, forgot
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, register, googleCallback } = useAuth();
  const redirect = searchParams.get('redirect') || '/tools/credentials';

  useEffect(() => { if (user) navigate(redirect); }, [user, navigate, redirect]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Handle Google OAuth callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      (async () => {
        setLoading(true);
        try {
          await googleCallback(sessionId);
          navigate(redirect);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [searchParams, googleCallback, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
        await register(name, email, password);
      } else {
        await login(email, password);
      }
      navigate(redirect);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const callbackUrl = `${window.location.origin}/auth?redirect=${encodeURIComponent(redirect)}`;
    window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?callback_url=${encodeURIComponent(callbackUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) setForgotSuccess(true);
      else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send reset email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="auth-page">
      <Header />
      <section className="pt-28 pb-16">
        <div className="container-width max-w-md">
          <Card className="p-6 md:p-8 border-border/50" data-testid="auth-card">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight" data-testid="auth-title">
                {mode === 'forgot' ? 'Forgot Password' : mode === 'login' ? 'Sign In' : 'Create Account'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'forgot' ? 'Enter your email to receive a reset link' : mode === 'login' ? 'Access your Credential Tracker' : 'Start tracking your credentials'}
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm" data-testid="auth-error">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Forgot Password Mode */}
            {mode === 'forgot' ? (
              forgotSuccess ? (
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">If an account with that email exists, we've sent a password reset link. Please check your inbox.</p>
                  <button onClick={() => { setMode('login'); setForgotSuccess(false); setError(''); }} className="text-sm text-primary hover:underline flex items-center gap-1 mx-auto" data-testid="back-to-login-link">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1.5 block">Email Address</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" className="pl-10 h-10" data-testid="forgot-email-input" />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-white h-11 rounded-lg" data-testid="forgot-submit-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}
                    </Button>
                  </form>
                  <div className="text-center mt-4">
                    <button onClick={() => { setMode('login'); setError(''); }} className="text-sm text-primary hover:underline flex items-center gap-1 mx-auto" data-testid="back-to-login-link">
                      <ArrowLeft className="h-3.5 w-3.5" /> Back to Sign In
                    </button>
                  </div>
                </>
              )
            ) : (
              <>
                {/* Google Login */}
                <Button type="button" variant="outline" onClick={handleGoogleLogin} disabled={loading} className="w-full mb-4 h-11 rounded-lg" data-testid="google-login-btn">
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-xs"><span className="bg-card px-3 text-muted-foreground">or</span></div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'register' && (
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1.5 block">Full Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input value={name} onChange={e => setName(e.target.value)} required placeholder="John Doe" className="pl-10 h-10" data-testid="auth-name-input" />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" className="pl-10 h-10" data-testid="auth-email-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder={mode === 'register' ? 'Min 6 characters' : 'Your password'} className="pl-10 h-10" data-testid="auth-password-input" />
                    </div>
                  </div>
                  {mode === 'login' && (
                    <div className="text-right">
                      <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </button>
                    </div>
                  )}
                  <Button type="submit" disabled={loading} className="w-full bg-primary text-white h-11 rounded-lg" data-testid="auth-submit-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'login' ? 'Sign In' : 'Create Account'}
                  </Button>
                </form>

                <div className="text-center mt-4">
                  <button
                    onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                    className="text-sm text-primary hover:underline"
                    data-testid="auth-toggle-mode"
                  >
                    {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                  </button>
                </div>
              </>
            )}
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default AuthPage;
